/**
 * Serviço para criação automática de clientes
 * Garante que clientes informados em transações sejam criados na tabela clients
 */

import { supabase } from '@/integrations/supabase/client';
import { v4 as uuidv4 } from 'uuid';

export interface ClientAutoCreateResult {
  success: boolean;
  clientId?: string;
  error?: string;
}

/**
 * Cria automaticamente um cliente na tabela clients se não existir
 */
export async function createClientIfNotExists(
  clientName: string,
  userId: string
): Promise<ClientAutoCreateResult> {
  if (!clientName || !userId) {
    return { success: false, error: 'Nome do cliente e ID do usuário são obrigatórios' };
  }

  try {
    // Verificar se cliente já existe
    const { data: existingClient, error: checkError } = await supabase
      .from('clients')
      .select('id, name')
      .eq('user_id', userId)
      .ilike('name', clientName.trim())
      .limit(1);

    if (checkError) {
      console.error('Erro ao verificar cliente existente:', checkError);
      return { success: false, error: checkError.message };
    }

    // Se cliente já existe, retornar ID existente
    if (existingClient && existingClient.length > 0) {
      return { 
        success: true, 
        clientId: existingClient[0].id 
      };
    }

    // Criar novo cliente
    const newClientId = uuidv4();
    const { data: newClient, error: createError } = await supabase
      .from('clients')
      .insert({
        id: newClientId,
        user_id: userId,
        name: clientName.trim(),
        document_type: 'cpf', // Default para clientes
        document_number: '',
        bank_account_type: 'corrente', // Valor padrão para satisfazer a constraint
        is_active: true
      })
      .select('id')
      .single();

    if (createError) {
      console.error('Erro ao criar cliente:', createError);
      return { success: false, error: createError.message };
    }

    console.log(`✅ Cliente "${clientName}" criado automaticamente com ID: ${newClientId}`);
    
    return { 
      success: true, 
      clientId: newClient.id 
    };

  } catch (error) {
    console.error('Erro inesperado ao criar cliente:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Erro desconhecido' 
    };
  }
}

/**
 * Sincroniza um cliente existente na transação com a tabela clients
 * Garante que o client_id seja preenchido corretamente
 */
export async function syncClientWithTransaction(
  transactionId: string,
  clientName: string,
  userId: string
): Promise<ClientAutoCreateResult> {
  if (!transactionId || !clientName || !userId) {
    return { success: false, error: 'ID da transação, nome do cliente e ID do usuário são obrigatórios' };
  }

  try {
    // Primeiro, garantir que o cliente exista na tabela clients
    const clientResult = await createClientIfNotExists(clientName, userId);
    
    if (!clientResult.success || !clientResult.clientId) {
      return clientResult;
    }

    // Atualizar a transação com o client_id
    const { error: updateError } = await supabase
      .from('transactions')
      .update({ 
        client_id: clientResult.clientId,
        // Manter o campo client para compatibilidade
        client: clientName 
      })
      .eq('id', transactionId);

    if (updateError) {
      console.error('Erro ao vincular cliente à transação:', updateError);
      return { success: false, error: updateError.message };
    }

    console.log(`✅ Cliente "${clientName}" vinculado à transação ${transactionId}`);
    
    return { 
      success: true, 
      clientId: clientResult.clientId 
    };

  } catch (error) {
    console.error('Erro inesperado ao sincronizar cliente:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Erro desconhecido' 
    };
  }
}

/**
 * Processa múltiplas transações para garantir clientes sincronizados
 */
export async function batchSyncClients(
  transactions: Array<{ id: string; client?: string; user_id: string }>
): Promise<{ success: number; errors: Array<{ transactionId: string; error: string }> }> {
  const results = {
    success: 0,
    errors: [] as Array<{ transactionId: string; error: string }>
  };

  for (const transaction of transactions) {
    if (transaction.client && transaction.user_id) {
      const result = await syncClientWithTransaction(
        transaction.id,
        transaction.client,
        transaction.user_id
      );

      if (result.success) {
        results.success++;
      } else {
        results.errors.push({
          transactionId: transaction.id,
          error: result.error || 'Erro desconhecido'
        });
      }
    }
  }

  return results;
}
